import './DeviceDetails.scss';
import React, { useEffect, useRef, useState } from 'react';
import { isEmpty, prop } from 'ramda';
import { useTranslation } from 'react-i18next';
import IconButton from '../iconButton/IconButton';
import ItemCard from '../ItemCard/ItemCard';
import { RESPONSE, ROUTES } from '../../js/_constants';
import Helpers from '../../js/helpers';
import EditDevice from './EditDevice';
import Modal from '../modal/Modal';
import AssignPlaylistOverlay from './AssignPlaylistOverlay';
import AssignGroupOverlay from './AssignGroupOverlay';
import { generalStore } from '../../store';
import { type Playlist, type ModuleType, type Device } from '../../api/types/index';
import { CollectionResponse } from '../../api/types/response';
import { type DropdownOptionType } from '../multiSelectDropdown/MultiSelectDropdown';
import DevicePlaylists from '../devicePlaylists/DevicePlaylists';
import DeviceRecentPlaylists from '../deviceRecentPlaylists/DeviceRecentPlaylists';
import DevicePlaylistGroup from '../devicePlaylistGroup/DevicePlaylistGroup';
import Loader from '../loader/Loader';
import Button from '../button/Button';

type DevicePatchResponse = Pick<
    Device,
    'title' | 'description' | 'playlists'
> & {
    dealership: string;
    moduleTypes: string[];
};

type DeviceDetailsProps = {
    data: Device;
    fallbackPlaylist: CollectionResponse<Playlist>;
    isFallbackLoading: boolean;
    isSaving: boolean;
    moduleTypes: ModuleType[];
    onPlaylistAssignment: (playlistIri: string) => void;
    onGroupAssignment: (groupIri: string) => void;
    onSave: (params: DevicePatchResponse) => void;
    isLoading: boolean;
};

const DeviceDetails = ({
    data,
    fallbackPlaylist,
    isFallbackLoading,
    isSaving,
    moduleTypes,
    onPlaylistAssignment,
    onGroupAssignment,
    onSave
}: DeviceDetailsProps) => {
    const { t } = useTranslation();
    const { setPageName } = generalStore();

    const {
        dealership,
        description,
        deviceNumber,
        serialNumber,
        thingName,
        moduleTypes: deviceModuleTypes,
        playlists: devicePlaylists,
        playlistGroups: deviceGroups,
        recentPlaylists,
        playlistGroups,
        title,
    } = data;

    const iri = data[RESPONSE.ID]

    const [isPlaylistModalOpen, setIsPlaylistModalOpen] = useState(false);

    const [selectedSource, setSelectedSource]= useState('my_playlists');

    useEffect(() => {
        setPageName(title);
    }, [data]);

    /**
     * On successful save, close the edit view
     */
    const prevIsSaving = useRef(false);
    useEffect(() => {
        if (prevIsSaving.current && !isSaving) {
            setIsEditing(false);
        }
        prevIsSaving.current = isSaving;
    }, [isSaving]);

    const preSelectedModuleTypeIdsIds = deviceModuleTypes.map(module => module[RESPONSE.ID]) as string[];

    const [isEditing, setIsEditing] = useState(false);
    const [selectedModuleTypeIds, setSelectedModuleTypeIds] = useState<
        string[]
    >(preSelectedModuleTypeIdsIds);
    const [deviceTitle, setTitle] = useState(title);
    const [deviceDescription, setDescription] = useState(description);

    const onEditClick = () => setIsEditing(true);
    const onCloseEditModalClick = () => setIsEditing(false);
    const onTitleChange = value => setTitle(value);
    const onDescChange = value => setDescription(value);

    const handleSaving = async () => {

        const compatiblePlaylists = devicePlaylists
            .filter(
                playlist =>
                    playlist?.moduleTypes?.findIndex(module =>
                        selectedModuleTypeIds.includes(module)
                    ) !== -1
            )
            .map(ele => {return { position: ele.position,
            contentPlaylist: ele.contentPlaylist[RESPONSE.ID]}});

        onSave({
            title: deviceTitle,
            description: deviceDescription,
            dealership: dealership[RESPONSE.ID],
            playlists: compatiblePlaylists,
            moduleTypes: selectedModuleTypeIds
        });
    };

    const handleModuleTypeSelection = (selectedOptions: DropdownOptionType[]) =>
        setSelectedModuleTypeIds(selectedOptions.map(prop('value')));

    const handlePlaylistModalOpen = () => setIsPlaylistModalOpen(true);
    const handlePlaylistModalClose = () => setIsPlaylistModalOpen(false);

    const renderModuleTypes = () => {
        const titles = deviceModuleTypes.map((type, index) => (
            <span key={type.title}>
                {index !== 0 && <span>, </span>}
                <span>{type.title}</span>
            </span>
        ));
        return titles;
    };

    const renderNoFallbackPlaylistMessage = () => {
        return (
            <p className="no-fallback-exists-message">
                {t('device_detail_no_fallback_message')}
            </p>
        );
    };

    const renderFallbackPlaylist = () => {
        const playlist = fallbackPlaylist[RESPONSE.EXPAND_COLLECTION][0];
        return (
            <>
                <p className="fallback-message">
                    {t('device_detail_fallback_message')}
                </p>
                <ItemCard
                    name="fallback-playlist-card"
                    thumbnail={playlist.thumbnail}
                    href={`${ROUTES.PLAYLISTS}/${playlist.uuid}`}
                    icon="media-playlist"
                    title={playlist.title}
                    subtitle={`${
                        playlist.language || 'EN'
                    } - ${Helpers.humanDuration(playlist.duration)} Min`}
                    description={playlist.description}
                    footer={`${t('playlist_last_edit')} : ${
                        playlist.updatedAt
                            ? Helpers.humanDate(playlist.updatedAt)
                            : ''
                    }`}
                />
            </>
        );
    };

    const renderFallback = () => {
        return isEmpty(fallbackPlaylist[RESPONSE.EXPAND_COLLECTION])
            ? renderNoFallbackPlaylistMessage()
            : renderFallbackPlaylist();
    };

    const incompatiblePlaylists = devicePlaylists.filter(
        playlist =>
            playlist?.moduleTypes?.findIndex(playlistModuleType =>
                selectedModuleTypeIds.includes(playlistModuleType)
            ) === -1
    );

    return (
        <div className="b-device-detail">
            <div className="details-list">
                <div className='device-header'>
                    <div className='device-id'>
                        <h3>{title}</h3>
                        <p className="location">{`${dealership.title} - ${dealership.kvps}`}</p>
                    </div>
                    <IconButton onClick={onEditClick} icon="edit" name="edit-device" />
                </div>
                {isEditing ? (
                    <EditDevice
                        deviceDescription={deviceDescription}
                        deviceModuleTypeIds={deviceModuleTypes}
                        deviceTitle={deviceTitle}
                        incompatiblePlaylists={incompatiblePlaylists}
                        isSaving={isSaving}
                        moduleTypeOptions={moduleTypes}
                        onCloseEditModalClick={onCloseEditModalClick}
                        onDescChange={onDescChange}
                        onModuleTypeSelection={handleModuleTypeSelection}
                        onSaveEditClick={handleSaving}
                        onTitleChange={onTitleChange}
                    />
                    ) : (
                    <>
                        <div className="details-list__list-element">
                            <h4 className="details-list__list-element-title">
                                {t('device_detail_title')}
                            </h4>
                            <p className="details-list__list-element-content">
                                {title}
                            </p>
                        </div>
                        <div className="details-list__list-element">
                            <h4 className="details-list__list-element-title">
                                {t('device_identifiers')}
                            </h4>
                            <p className="details-list__list-element-content">
                                {deviceNumber && deviceNumber}
                                {serialNumber && <><br /><br />{serialNumber}</>}
                                {thingName && <><br /><br />{thingName}</>}
                            </p>
                        </div>
                        <div className="details-list__list-element">
                            <h4 className="details-list__list-element-title">
                                {t('device_detail_description')}
                            </h4>
                            <p className="details-list__list-element-content">
                                {description}
                            </p>
                        </div>

                        <div className="details-list__list-element">
                            <h4 className="details-list__list-element-title">
                                {t('playlist_module')}
                            </h4>
                            {deviceModuleTypes && (
                                <p className="details-list__list-element-content">
                                    {renderModuleTypes()}
                                </p>
                            )}
                        </div>
                    </>)}
            </div>
            <div className='device-playlists-header'>
                <h3>{t('device_playlists')}</h3>
                <IconButton
                    onClick={handlePlaylistModalOpen}
                    icon="system-add-large"   
                    name="add-playlist"
                />
            </div>
            <div className="b-playlists-wrapper">
                <DevicePlaylists items={devicePlaylists} iri={iri} />
                <DevicePlaylistGroup items={playlistGroups} iri={iri} />
                <DeviceRecentPlaylists items={recentPlaylists} iri={iri} />
                <p className="playlist-fallback">
                    {t('device_detail_fallback_playlist')}
                </p>
                <div className="fallback-wrapper">
                    {isFallbackLoading ? (
                        <Loader className="fallback-wrapper__loader" isInline />
                    ) : (
                        renderFallback()
                    )}
                </div>
            </div>
            <Modal
                show={isPlaylistModalOpen}
                position="left"
                onClose={handlePlaylistModalClose}
            >
                <Modal.Title>
                    {t('device_detail_playlist_modal_title')}
                    <div className="button-container">
                        <Button
                            name="my-playlists-button"
                            clickHandler={() => setSelectedSource('my_playlists')}
                            text={t('my_playlists')}
                            secondaryButton={selectedSource !== 'my_playlists'}
                        />
                        <Button
                            name="market-playlists-button"
                            clickHandler={() => setSelectedSource('market_playlists')}
                            text={t('market_playlists')}
                            secondaryButton={selectedSource !== 'market_playlists'}
                        />
                        <Button
                            name="playlist-groups-button"
                            clickHandler={() => setSelectedSource('groups')}
                            text={t('playlist_groups')}
                            secondaryButton={selectedSource !== 'groups'}
                        />
                    </div>
                </Modal.Title>
                <Modal.Content>
                    {selectedSource === 'my_playlists' && <AssignPlaylistOverlay
                        onItemClick={onPlaylistAssignment}
                        isFilterable
                        selectedPlaylists={devicePlaylists}
                        device={data}
                    />}
                    {selectedSource === 'market_playlists' && <AssignPlaylistOverlay
                        onItemClick={onPlaylistAssignment}
                        isFilterable
                        selectedPlaylists={devicePlaylists}
                        device={data}
                        market
                    />}
                    {selectedSource === 'groups' && <AssignGroupOverlay
                        onItemClick={onGroupAssignment}
                        isFilterable
                        selectedGroups={deviceGroups}
                    />}
                </Modal.Content>
            </Modal>
        </div>
    );
};

export default DeviceDetails;
